import React from 'react'
import { connect } from 'react-redux'
import { Dispatch, AnyAction } from 'redux'

import { RootStoreState } from '../../root/index'
import * as actions from '../actions'
import * as apiActions from '../../api/actions'
import { navigateToScreen, navigateToCollection } from '../../navigation/actions'
import { CollectionItem, Collection, ResponseError } from '../../api/types'
import { getCurrentLanguageCode } from '../../settings/functions'
import { getConfiguration } from '../../configuration/index'
import { INITIAL_STATE } from '../../lockapp/reducer'
import { CouponCode, CodeStatus } from '../../lockapp/types'
import { RedeemCode } from 'mytours-read-api-typescript'

/**
 * Defines the own props that this container requires to render a collection
 */
export interface CollectionListContainerOwnProps {
	collectionId: number
	collectionVersion?: number
	accountId?: number
	onBackAction?: (params: any) => void
}

export interface Props extends CollectionListContainerOwnProps {
	collection?: Collection
	items: CollectionItem[]
	loading: boolean
	error?: ResponseError
	language: string
	lockCollection?: CodeStatus
}

export interface Actions {
	navigateToScreen: (item: CollectionItem, parentItemId: number) => void
	navigateToCollection: (item: CollectionItem, parentItemId: number) => void
	loadCollectionItems: (collectionId: number, collectionVersion: number, language: string, accountId?: number) => void
	loadCollectionDetails: (collectionId: number, collectionVersion: number, language: string, accountId?: number) => void
	clearError: (collectionId: number) => void
	validateCode: (couponCode: CouponCode, linkedId: number) => void
}

// NB had to extend ContainerOwnProps as was getting TS error for web
const mapStateToProps = <P extends CollectionListContainerOwnProps>(state: RootStoreState, ownProps: P): Props => {
	const params = getConfiguration().convertCollectionListContainerOwnProps(ownProps)

	// get collection details so we know how to display some parts of the UI
	const collectionDetails = state.collectionlist.collectionDetails.get(params.collectionId)
	const collection: Collection | undefined =
		collectionDetails && collectionDetails.details ? collectionDetails.details : undefined

	const collectionItems = state.collectionlist.collectionItems.get(params.collectionId)
	const items: CollectionItem[] = collectionItems && collectionItems.items ? collectionItems.items : []

	const language: string = getCurrentLanguageCode(state)

	const manifestCollectionVersion = state.settings.appManifest?.collections?.find(
		item => item.id === params.collectionId,
	)?.version

	return {
		collectionId: params.collectionId,
		collectionVersion: params.collectionVersion || manifestCollectionVersion,
		collection,
		items,
		loading: collectionItems ? collectionItems.loading : false,
		error: collectionItems ? collectionItems.error : undefined,
		language,
		lockCollection: (state.lockapp.linkedType === RedeemCode.LinkedTypeEnum.Collection && state.lockapp.linkedId === params.collectionId) ? state.lockapp.collection :  INITIAL_STATE.collection,
	}
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): Actions => ({
	navigateToScreen: (item: CollectionItem, parentItemId: number) => {
		dispatch(navigateToScreen({ item, parentItemId }))
	},
	navigateToCollection: (item: CollectionItem, parentItemId: number) => {
		dispatch(navigateToCollection({ item, parentItemId }))
	},
	loadCollectionItems: (id: number, version: number, language: string, accountId?: number) => {
		dispatch(actions.loadCollectionItems({ id, version, language, subtype: 'list', accountId }))
	},
	loadCollectionDetails: (id: number, version: number, language: string, accountId?: number) => {
		dispatch(actions.loadCollectionDetails({ id, version, language, accountId }))
	},
	clearError: (id: number) => {
		dispatch(actions.clearLoadCollectionErrorAction(id))
	},
	validateCode: (couponCode: CouponCode, linkedId: number) => {
		const platform = getConfiguration().getPlatform()
		const deviceUUID = getConfiguration().getDeviceUUID()

		dispatch(
			apiActions.validateCode.started({
				linkedId,
				linkedType: RedeemCode.LinkedTypeEnum.Collection,
				devicePlatform: platform,
				deviceUdid: deviceUUID,
				couponCode: couponCode.code,
				expireAt: couponCode.expireAt,
			}),
		)
	},
})

export default (Component: React.ComponentType<Props & Actions>) =>
	connect(mapStateToProps, mapDispatchToProps)(Component)
